import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDelhiRecords,
  syncTaskStatus,
  deleteDelhiRecord,
  bulkDeleteDelhiRecords,
  importDelhiRecords,
  exportDelhiRecords,
  updateDelhiRecord,
  resetImportDelhiRecords,
  getFieldExecutives,
  getDepartments,
  getFOS,
} from "../actions/delhiAction";
import { getTaskById } from "../actions/taskActions";
import Button from "../components/Button";
import Loading from "../components/Loading";
import Pagination from "../components/Pagination";
import Table from "../components/Table";
import DriveFilters, {
  driveFilterSelectClass,
} from "../components/DriveFilters";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  Edit,
  Trash2,
  Upload,
  Download,
  Check,
  X,
  Trash,
  Eye,
  Copy,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const BIZ_LOAN_CUSTOMER_ID = "668d1884814ae1cdbde463f1";

// Helper to get last 7 IST dates in YYYY-MM-DD including today
function getLast7IstDatesIncludingToday() {
  const dates = [];
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const istOffset = 5.5 * 60 * 60 * 1000;
  for (let i = 0; i < 7; i++) {
    const istTime = new Date(utc + istOffset - i * 24 * 60 * 60 * 1000);
    const yyyy = istTime.getFullYear();
    const mm = String(istTime.getMonth() + 1).padStart(2, "0");
    const dd = String(istTime.getDate()).padStart(2, "0");
    dates.push(`${yyyy}-${mm}-${dd}`);
  }
  return dates;
}

// Helper to get last 30 IST dates in YYYY-MM-DD including today
function getLast30IstDatesIncludingToday() {
  const dates = [];
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const istOffset = 5.5 * 60 * 60 * 1000;
  // Include today (i = 0) and go back 29 more days for total of 30 days
  for (let i = 0; i < 30; i++) {
    const istTime = new Date(utc + istOffset - i * 24 * 60 * 60 * 1000);
    const yyyy = istTime.getFullYear();
    const mm = String(istTime.getMonth() + 1).padStart(2, "0");
    const dd = String(istTime.getDate()).padStart(2, "0");
    dates.push(`${yyyy}-${mm}-${dd}`);
  }
  return dates;
}

const DelhiPage = () => {
  const dispatch = useDispatch();
  const { getDelhiRecordsData, loadingGetDelhiRecords } = useSelector(
    (state) => state.getDelhiRecords || {},
  );
  const {
    importDelhiRecordsData,
    loadingImportDelhiRecords,
    errorImportDelhiRecords,
  } = useSelector((state) => state.importDelhiRecords || {});
  const [data, setData] = useState(getDelhiRecordsData?.data || []);
  const [total, setTotal] = useState(getDelhiRecordsData?.total || 0);
  const loading = loadingGetDelhiRecords || false;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [file, setFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const inputRef = useRef(null);
  const tableRef = useRef(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState("");
  const [editRemarkValue, setEditRemarkValue] = useState("");
  const [remarkUpdateLoading, setRemarkUpdateLoading] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const { getFieldExecutivesData, loadingGetFieldExecutives } = useSelector(
    (state) => state.getFieldExecutives || {},
  );

  const [tatEditModalOpen, setTatEditModalOpen] = useState(false);
  const [tatEditValue, setTatEditValue] = useState("");
  const [tatUpdateLoading, setTatUpdateLoading] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  // const [refreshIntervalTime, setRefreshIntervalTime] = useState(30000);

  const [importDate, setImportDate] = useState("");
  const [dates, setDates] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [taskIdEditModalOpen, setTaskIdEditModalOpen] = useState(false);
  const [taskIdEditValue, setTaskIdEditValue] = useState("");
  const [taskIdUpdateLoading, setTaskIdUpdateLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [taskStatusEditModalOpen, setTaskStatusEditModalOpen] = useState(false);
  const [taskStatusEditValue, setTaskStatusEditValue] = useState("");
  const [taskStatusUpdateLoading, setTaskStatusUpdateLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const [autoRefresh, setAutoRefresh] = useState(true);
  // const [refreshInterval, setRefreshInterval] = useState(15000);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const intervalRef = useRef(null);

  const [departmentFilter, setDepartmentFilter] = useState("");
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const [fosFilter, setFosFilter] = useState("");
  const [fosList, setFosList] = useState([]);
  const [loadingFOS, setLoadingFOS] = useState(false);

  const [fosEditModalOpen, setFosEditModalOpen] = useState(false);
  const [fosSearchValue, setFosSearchValue] = useState("");
  const [fosUpdateLoading, setFosUpdateLoading] = useState(false);
  const [fieldExecutives, setFieldExecutives] = useState([]);
  const [fosSearchLoading, setFosSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentTime, setCurrentTime] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [delhiViewOption, setDelhiViewOption] = useState(true);
  const [delhiAddOption, setDelhiAddOption] = useState(true);
  const [delhiUpdateOption, setDelhiUpdateOption] = useState(true);
  const [delhiDeleteOption, setDelhiDeleteOption] = useState(true);

  // Task customer type state for conditional date range
  const [taskCustomerId, setTaskCustomerId] = useState(null);
  const [isBizLoanTask, setIsBizLoanTask] = useState(false);
  const fallbackAttemptedRef = useRef(new Set()); // Track which searches have had fallback attempted

  const fields = [
    "sno",
    "task_id",
    "task_status",
    "code",
    "los",
    "name",
    "fi_type",
    "product",
    "address",
    "pincode",
    "date",
    "time",
    "fos",
    "zone",
    "type",
    "branch",
    "department",
    "tat",
    "punch_time",
    "punch_date",
    "remark",
    "status",
    "reason",
    "reviewer_name",
    "time_in",
  ];
  const PrimaryButton = ({
    children,
    onClick,
    disabled,
    className,
    loading,
  }) => (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      text={children}
      custom={`bg-blue-500 hover:bg-blue-700 ${className}`}
    />
  );

  const SecondaryButton = ({ children, onClick, className }) => (
    <Button
      onClick={onClick}
      text={children}
      custom={`bg-gray-500 hover:bg-gray-700 ${className}`}
    />
  );
  const DangerButton = ({ children, onClick, className, loading }) => (
    <Button
      onClick={onClick}
      disabled={loading}
      text={children}
      custom={`bg-red-500 hover:bg-red-700 ${className}`}
    />
  );

  const ModalActionButtons = ({ children, className }) => (
    <div className={`flex gap-2 justify-end ${className}`}>{children}</div>
  );

  const navigate = useNavigate();
  const userLogin = useSelector((state) => state.userLogin);
  const { loadingUserInfo, userInfo } = userLogin;

  useEffect(() => {
    // Check if user info exists
    if (!userInfo) {
      navigate("/");
    }
  }, [userInfo, navigate]);

  // Check for role permissions
  const getRoleInfo = useSelector((state) => state.getRoleInfo);
  const { loadingGetRole, getRoleData } = getRoleInfo;

  useEffect(() => {
    if (getRoleData) {
      // Use driveNbfcDelhi for Delhi page access
      setDelhiViewOption(getRoleData.driveNbfcDelhi);
      setDelhiAddOption(getRoleData.driveNbfcDelhi); // Same permission for add operations
      setDelhiUpdateOption(getRoleData.driveNbfcDelhi); // Same permission for update operations
      setDelhiDeleteOption(getRoleData.driveNbfcDelhi); // Same permission for delete operations

      if (!getRoleData.driveNbfcDelhi) {
        toast("Not Authorized", {
          type: "error",
          hideProgressBar: true,
          autoClose: 2000,
        });
        navigate("/");
      }
    }
  }, [userInfo, getRoleData, navigate]);

  const getCurrentISTDisplay = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    const currentISTTime = new Date(utc + istOffset);

    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    };

    return currentISTTime.toLocaleDateString("en-IN", options);
  };

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "unassigned", label: "Unassigned" },
    { value: "visit pending", label: "Visit Pending" },
    { value: "finalisation pending", label: "Finalisation Pending" },
    { value: "in progress", label: "In Progress" },
    { value: "mapping", label: "Mapping" },
    { value: "not fired", label: "Not Fired" },
    { value: "waived", label: "Waived" },
    { value: "completed", label: "Completed" },
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "LOS", label: "LOS" },
    { value: "LS", label: "LS" },
    { value: "MAIL", label: "MAIL" },
  ];

  const filterControls = [
    {
      key: "date",
      label: "Date",
      control: (
        <select
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className={driveFilterSelectClass}
        >
          <option value="">Select Date</option>
          {dates.map((date) => (
            <option key={date} value={date}>
              {date}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "status",
      label: "Status",
      control: (
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilter(e.target.value)}
          className={driveFilterSelectClass}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "type",
      label: "Type",
      control: (
        <select
          value={typeFilter}
          onChange={(e) => handleTypeFilter(e.target.value)}
          className={driveFilterSelectClass}
        >
          {typeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "department",
      label: "Department",
      control: (
        <select
          value={departmentFilter}
          onChange={(e) => handleDepartmentFilter(e.target.value)}
          className={`${driveFilterSelectClass} ${
            loadingDepartments
              ? "disabled:bg-gray-50 disabled:cursor-not-allowed"
              : ""
          }`}
          disabled={loadingDepartments}
        >
          <option value="">All Departments</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "fos",
      label: "FOS",
      control: (
        <select
          value={fosFilter}
          onChange={(e) => handleFosFilter(e.target.value)}
          className={`${driveFilterSelectClass} ${
            loadingFOS ? "disabled:bg-gray-50 disabled:cursor-not-allowed" : ""
          }`}
          disabled={loadingFOS}
        >
          <option value="">All FOS</option>
          {fosList.map((fos) => (
            <option key={fos} value={fos}>
              {fos}
            </option>
          ))}
        </select>
      ),
    },
  ];

  const columns = [
    { field: "sno", header: "S.no" },
    {
      field: "task_id",
      header: "Task ID",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[60px]">
            {rowData.task_id || "-"}
          </span>
          <button
            onClick={() => {
              setTaskIdEditValue(rowData.task_id || "");
              setFormData(rowData);
              setTaskIdEditModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit Task ID"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    {
      field: "task_status",
      header: "Task Status",
      body: (rowData) => {
        const status = rowData.task_status;

        // Define color mapping
        const getStatusColor = (status) => {
          if (!status) return "bg-gray-100 text-gray-600"; // Default for empty

          const statusLower = status.toLowerCase().trim();

          switch (statusLower) {
            case "unassigned":
              return "bg-red-100 text-red-800 border-red-200";
            case "visit pending":
              return "bg-orange-100 text-orange-800 border-orange-200";
            case "finalisation pending":
              return "bg-yellow-100 text-yellow-800 border-yellow-200";
            case "completed":
              return "bg-green-100 text-green-800 border-green-200";
            default:
              return "bg-gray-100 text-gray-600 border-gray-200";
          }
        };

        return (
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(status)}`}
              title={status || "No Status"}
            >
              {status || "-"}
            </span>
            {/* <button
              onClick={() => {
                setTaskStatusEditValue(rowData.task_status || '');
                setFormData(rowData);
                setTaskStatusEditModalOpen(true);
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="Edit Task Status"
            >
              <Edit className="h-4 w-4" />
            </button> */}
          </div>
        );
      },
    },
    { field: "initiation_date", header: "Imported Date" },
    { field: "code", header: "CODE" },
    { field: "los", header: "LOS" },
    { field: "name", header: "Name" },
    { field: "fi_type", header: "FI TYPE" },
    { field: "product", header: "PRODUCT" },
    {
      field: "address",
      header: "ADDRESS",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[100px]">
            {rowData.address?.length > 20
              ? rowData.address.slice(0, 20) + "..."
              : rowData.address || "-"}
          </span>
          <button
            onClick={() => {
              setSelectedAddress(rowData.address || "No address available");
              setAddressModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="View Full Address"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    { field: "pincode", header: "pincode" },
    { field: "date", header: "DATE" },
    { field: "time", header: "Time" },
    {
      field: "fos",
      header: "FOS",
      body: (rowData) => {
        // Hide FOS if status is "Sent To Bank"
        if (rowData.task_status === "Sent To Bank") {
          return <div className="text-gray-400">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[100px]">{rowData.fos || "-"}</span>
            <button
              onClick={() => {
                setFosSearchValue(rowData.fos || "");
                setFormData(rowData);
                setFosEditModalOpen(true);
                // Fetch initial field executives
                dispatch(getFieldExecutives(""));
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="Edit FOS"
            >
              <Edit className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },

    { field: "zone", header: "zone" },
    { field: "type", header: "TYPE" },
    { field: "branch", header: "BRANCH" },
    { field: "department", header: "DEPARTMENT" },
    {
      field: "tat",
      header: "TAT",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[60px]">{rowData.tat || "-"}</span>
          <button
            onClick={() => {
              setTatEditValue(rowData.tat || "");
              setFormData(rowData);
              setTatEditModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit TAT"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    { field: "punch_time", header: "PUNCH TIME" },
    { field: "punch_date", header: "PUNCH DATE" },
    {
      field: "remark",
      header: "REMARK",
      body: (rowData) => {
        const remark = rowData.remark;
        return (
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[100px]">
              {remark
                ? remark.length > 20
                  ? remark.slice(0, 20) + "..."
                  : remark
                : "-"}
            </span>
            <button
              onClick={() => {
                setSelectedRemark(remark || "");
                setEditRemarkValue(remark || "");
                setFormData(rowData);
                setViewModalOpen(true);
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="View/Edit remark"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    {
      field: "status",
      header: "STATUS",
      body: (rowData) => (
        <span className="text-sm text-gray-700">{rowData.status || "-"}</span>
      ),
    },
    {
      field: "reason",
      header: "RESON",
      body: (rowData) => (
        <span
          className="text-sm text-gray-700 max-w-[150px] truncate block"
          title={rowData.reason || ""}
        >
          {rowData.reason
            ? rowData.reason.length > 30
              ? rowData.reason.slice(0, 30) + "..."
              : rowData.reason
            : "-"}
        </span>
      ),
    },
    { field: "reviewer_name", header: "Reviewer Name" },
    { field: "time_in", header: "TIME IN" },
  ];

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Remark copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy remark");
    }
  };

  const handleTypeFilter = (selectedType) => {
    console.log("Type filter changed to:", selectedType);
    setTypeFilter(selectedType);
    setPage(1);
  };

  const handleDepartmentFilter = (selectedDepartment) => {
    console.log("Department filter changed to:", selectedDepartment);
    setDepartmentFilter(selectedDepartment);
    setPage(1);
  };

  const handleFosFilter = (selectedFos) => {
    console.log("FOS filter changed to:", selectedFos);
    setFosFilter(selectedFos);
    setPage(1);
  };

  useEffect(() => {
    let interval;
    if (tatEditModalOpen) {
      interval = setInterval(() => {
        setCurrentTime(getCurrentISTDisplay());
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tatEditModalOpen]);

  useEffect(() => {
    console.log("getFieldExecutivesData changed:", getFieldExecutivesData);
    if (getFieldExecutivesData?.data) {
      console.log("Setting fieldExecutives:", getFieldExecutivesData.data);
      setFieldExecutives(getFieldExecutivesData.data);
    }
  }, [getFieldExecutivesData]);

  const refreshData = async () => {
    try {
      // Sync task statuses first
      console.log("Syncing task statuses, FOS, and remarks before refresh...");
      const syncResult = await dispatch(syncTaskStatus());

      // If sync returned updated records, update local state first
      if (
        syncResult &&
        syncResult.updatedRecords &&
        syncResult.updatedRecords.length > 0
      ) {
        setData((prevData) => {
          const updatedData = [...prevData];
          syncResult.updatedRecords.forEach((update) => {
            const recordIndex = updatedData.findIndex(
              (record) => record._id === update._id,
            );
            if (recordIndex !== -1) {
              // Update status if it changed
              if (update.new_status) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  task_status: update.new_status,
                };
              }

              // Update FOS if it changed
              if (update.new_fos) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  fos: update.new_fos,
                };
              }

              // Update remark if it changed
              if (update.new_remark) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  remark: update.new_remark,
                };
              }
              if (update.new_status_field) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  status: update.new_status_field,
                };
              }

              if (update.new_reason) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  reason: update.new_reason,
                };
              }

              // Similarly, in performRealtimeSync function around line 390, add the same updates:
              if (update.new_status_field) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  status: update.new_status_field,
                };
              }

              if (update.new_reason) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  reason: update.new_reason,
                };
              }
            }
          });
          return updatedData;
        });
      }

      // Then fetch updated data
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;
      if (typeFilter) query += `&typeFilter=${typeFilter}`;
      if (departmentFilter) query += `&departmentFilter=${departmentFilter}`;
      if (fosFilter) query += `&fosFilter=${fosFilter}`;
      dispatch(getDelhiRecords(query));
    } catch (error) {
      console.error("Error during sync in refreshData:", error);
      // Still fetch data even if sync fails
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;
      if (typeFilter) query += `&typeFilter=${typeFilter}`;
      if (departmentFilter) query += `&departmentFilter=${departmentFilter}`;
      if (fosFilter) query += `&fosFilter=${fosFilter}`;
      dispatch(getDelhiRecords(query));
    }
  };

  const handleFosSearch = useCallback(
    async (searchTerm) => {
      console.log("handleFosSearch called with:", searchTerm);
      setFosSearchValue(searchTerm);

      if (searchTerm.length > 0) {
        try {
          setFosSearchLoading(true);
          console.log("Dispatching getFieldExecutives...");

          const result = await dispatch(getFieldExecutives(searchTerm));
          console.log("getFieldExecutives result:", result);

          // Update state immediately with the result
          if (result && result.data) {
            setFieldExecutives(result.data);
            setShowSuggestions(true);
          } else {
            setFieldExecutives([]);
            setShowSuggestions(false);
          }
        } catch (error) {
          console.error("Error searching field executives:", error);
          setFieldExecutives([]);
          setShowSuggestions(false);
        } finally {
          setFosSearchLoading(false);
        }
      } else {
        setFieldExecutives([]);
        setShowSuggestions(false);
      }
    },
    [dispatch],
  );

  // Sync on page load/refresh only (automatic interval sync disabled)
  useEffect(() => {
    const performRealtimeSync = async () => {
      try {
        console.log("Starting realtime sync...");

        // Call the existing sync endpoint
        const syncResult = await dispatch(syncTaskStatus());

        // If there are updated records, update the local state
        if (
          syncResult &&
          syncResult.updatedRecords &&
          syncResult.updatedRecords.length > 0
        ) {
          console.log(
            `Found ${syncResult.updatedRecords.length} updated records`,
          );

          // Update local data state with new statuses, FOS, and remarks
          setData((prevData) => {
            const updatedData = [...prevData];
            syncResult.updatedRecords.forEach((update) => {
              const recordIndex = updatedData.findIndex(
                (record) => record._id === update._id,
              );
              if (recordIndex !== -1) {
                // Update status if it changed
                if (update.new_status) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    task_status: update.new_status,
                  };
                }

                // Update FOS if it changed
                if (update.new_fos) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    fos: update.new_fos,
                  };
                }

                // Update remark if it changed
                if (update.new_remark) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    remark: update.new_remark,
                  };
                }
              }
            });
            return updatedData;
          });

          // Enhanced toast notification
          const statusUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_status,
          ).length;
          const fosUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_fos,
          ).length;
          const remarkUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_remark,
          ).length;
          const statusFieldUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_status_field,
          ).length; // Add this
          const reasonUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_reason,
          ).length; // Add this

          let message = "";
          if (statusUpdates > 0) message += `${statusUpdates} status`;
          if (fosUpdates > 0)
            message += `${message ? ", " : ""}${fosUpdates} FOS`;
          if (remarkUpdates > 0)
            message += `${message ? ", " : ""}${remarkUpdates} remark`;
          if (statusFieldUpdates > 0)
            message += `${message ? ", " : ""}${statusFieldUpdates} status field`; // Add this
          if (reasonUpdates > 0)
            message += `${message ? ", " : ""}${reasonUpdates} reason`; // Add this

          // if (message) {
          //   toast.success(`${message} updated automatically`, {
          //     position: "bottom-right",
          //     autoClose: 3000,
          //   });
          // }
        }

        setLastRefresh(new Date());
      } catch (error) {
        console.error("Realtime sync failed:", error);
        // Fallback to full refresh if realtime sync fails
        refreshData();
      }
    };

    // Sync ONLY on initial page load (not on filter/page changes)
    // This prevents CPU spikes on every interaction
    performRealtimeSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch departments and FOS on component mount
  // Fetch departments and FOS based on current filters (importDate, isBizLoanTask)
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const result = await dispatch(getDepartments());
        if (result && result.data) {
          setDepartments(result.data);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        setDepartments([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    const fetchFOS = async () => {
      try {
        setLoadingFOS(true);
        // Build query with current date range to get FOS for visible tasks only
        let dateToUse = "";

        // Determine which date range to use
        if (isBizLoanTask) {
          // Biz Loan: use 30 days
          const last30Days = getLast30IstDatesIncludingToday();
          dateToUse = last30Days.join(",");
        } else if (filterDate) {
          // User selected a specific date
          dateToUse = filterDate;
        } else {
          // Default: use today's date (or current importDate)
          dateToUse = importDate;
        }

        console.log("Fetching FOS with date range:", dateToUse);
        // Pass importDate as query parameter to backend
        const result = await dispatch(getFOS(dateToUse));
        if (result && result.data) {
          setFosList(result.data);
          console.log(
            "FOS list updated for date range:",
            dateToUse,
            "FOS:",
            result.data,
          );
        }
      } catch (error) {
        console.error("Error fetching FOS:", error);
        setFosList([]);
      } finally {
        setLoadingFOS(false);
      }
    };

    fetchDepartments();
    fetchFOS();
    // Re-fetch FOS when date range or Biz Loan status changes
  }, [dispatch, importDate, filterDate, isBizLoanTask]);

  useEffect(() => {
    // Determine if the logged-in user is a Biz Loan customer
    const isBizLoanUser = userInfo?.customers?.some(
      (c) => (c._id || c) === BIZ_LOAN_CUSTOMER_ID,
    );
    console.log("Delhi Page: User Biz Loan status:", isBizLoanUser);

    // Set dates for the dropdown (always show 30 days for flexibility)
    const dropdownDates = getLast30IstDatesIncludingToday();
    setDates(dropdownDates);

    // By default, set importDate based on user type
    let defaultRange;
    if (isBizLoanUser) {
      // Biz Loan customers get 30 days by default
      defaultRange = getLast30IstDatesIncludingToday().join(",");
      setIsBizLoanTask(true);
    } else {
      // Other customers get 7 days by default
      defaultRange = getLast7IstDatesIncludingToday().join(",");
      setIsBizLoanTask(false);
    }

    // Clear any existing filters and set to default range
    setImportDate(defaultRange);
    setFilterDate(""); // Clear dropdown selection
    setStatusFilter(""); // Clear status filter
    setSearch(""); // Clear search
    setPage(1); // Reset to first page
    setTypeFilter("");
    setDepartmentFilter("");
    setFosFilter("");
  }, []);

  useEffect(() => {
    if (!importDate) return;
    const validPage = Math.max(1, parseInt(page) || 1);
    if (validPage !== page) {
      setPage(validPage);
      return;
    }
    let query = `search=${encodeURIComponent(search)}&page=${validPage}&limit=10&_t=${Date.now()}`;

    // Determine date range: Prioritize user selection, then Biz Loan 30-day default, then standard 7-day default
    if (filterDate) {
      // User selected a specific date
      query += `&importDate=${filterDate}`;
    } else if (isBizLoanTask) {
      // For Biz Loan customers, default to 30 days if no date selected
      const last30Days = getLast30IstDatesIncludingToday();
      query += `&importDate=${last30Days.join(",")}`;
    } else if (search) {
      // If searching, do not restrict by date
      query += ``;
    } else {
      // Default: use 7-day range for regular customers
      query += `&importDate=${importDate}`;
    }

    if (statusFilter) query += `&status=${encodeURIComponent(statusFilter)}`;
    if (typeFilter) query += `&typeFilter=${encodeURIComponent(typeFilter)}`;
    if (departmentFilter)
      query += `&departmentFilter=${encodeURIComponent(departmentFilter)}`;
    if (fosFilter) query += `&fosFilter=${encodeURIComponent(fosFilter)}`;

    console.log("Making API call with query:", query);
    dispatch(getDelhiRecords(query));
  }, [
    dispatch,
    search,
    page,
    importDate,
    filterDate,
    statusFilter,
    typeFilter,
    departmentFilter,
    fosFilter,
    isBizLoanTask,
  ]); // Add fosFilter here

  useEffect(() => {
    setData(getDelhiRecordsData?.data || []);
    setTotal(getDelhiRecordsData?.total || 0);
  }, [getDelhiRecordsData]);

  useEffect(() => {
    if (importDelhiRecordsData && !loadingImportDelhiRecords) {
      toast.success("Import successful!");
      dispatch(resetImportDelhiRecords());
      refreshData();
    }
  }, [importDelhiRecordsData, loadingImportDelhiRecords, dispatch]);

  // Check task customer type when data changes
  useEffect(() => {
    const checkTaskCustomer = async () => {
      if (getDelhiRecordsData?.data && getDelhiRecordsData.data.length > 0) {
        // Found results, record this
        const firstRecord = getDelhiRecordsData.data.find(
          (record) => record.task_id,
        );

        if (firstRecord?.task_id && firstRecord.task_id !== taskCustomerId) {
          console.log("Checking customer for task_id:", firstRecord.task_id);
          setTaskCustomerId(firstRecord.task_id);
          console.log(
            "Task found in results with Biz Loan status:",
            isBizLoanTask,
          );

          if (firstRecord.task_id === BIZ_LOAN_CUSTOMER_ID && !isBizLoanTask) {
            setIsBizLoanTask(true);
          }
        }

        // Check if loaded records are from Biz Loan customer (based on task details if available)
        // If this is initial load (no search) and we found Biz Loan records, switch to 30-day view
        if (!search && !isBizLoanTask && firstRecord) {
          // Check if task has customer field that matches Biz Loan
          // This would need to be added to the record on backend if not already present
          console.log(
            "Initial load detected, checking for Biz Loan customer records",
          );
        }
      } else if (
        getDelhiRecordsData?.data?.length === 0 &&
        search &&
        !isBizLoanTask
      ) {
        // No results found with 7-day search, attempt 30-day fallback once per search
        const searchKey = `${search}-fallback`;

        if (!fallbackAttemptedRef.current.has(searchKey)) {
          console.log(
            "No results found with 7-day search, attempting 30-day fallback for:",
            search,
          );
          fallbackAttemptedRef.current.add(searchKey);
          // Enable 30-day search for this search term
          setIsBizLoanTask(true);
        } else {
          console.log(
            "Fallback already attempted for this search term:",
            search,
          );
        }
      } else if (
        getDelhiRecordsData?.data?.length === 0 &&
        !search &&
        !isBizLoanTask
      ) {
        // No results found on initial load with today's date - try 30 days as fallback
        // This handles Biz Loan customers who might not have today's data
        const fallbackKey = "initial-load-fallback";
        if (!fallbackAttemptedRef.current.has(fallbackKey)) {
          console.log(
            "No results found on initial load, attempting 30-day fallback",
          );
          fallbackAttemptedRef.current.add(fallbackKey);
          setIsBizLoanTask(true);
        }
      }
    };

    checkTaskCustomer();
  }, [getDelhiRecordsData, search, isBizLoanTask]);

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    setPage(1);

    // Reset isBizLoanTask when user starts a new search
    // It will be determined based on the actual data returned
    if (searchTerm && searchTerm.trim()) {
      // Start with 7-day search (default)
      setIsBizLoanTask(false);
      // Clear fallback tracking for this new search so it can attempt fallback if needed
      const searchKey = `${searchTerm}-fallback`;
      fallbackAttemptedRef.current.delete(searchKey);
      console.log("Starting new search, will use 7-day range by default");
    } else {
      // If search is cleared, reset to 7-day range
      setIsBizLoanTask(false);
      // Clear all fallback attempts when search is cleared
      fallbackAttemptedRef.current.clear();
    }
  };

  const handleStatusFilter = (selectedStatus) => {
    console.log("Status filter changed to:", selectedStatus); // Debug log
    setStatusFilter(selectedStatus);
    setPage(1);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = data.map((row) => row._id); // Only store IDs
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (rowId, checked) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, rowId]); // Only store the ID
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== rowId));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select rows to delete");
      return;
    }
    try {
      setBulkDeleteLoading(true);
      // Extract only the _id from selectedRows if they are objects
      const idsArray = selectedRows.map((row) =>
        typeof row === "object" ? row._id : row,
      );
      await dispatch(bulkDeleteDelhiRecords(idsArray));
      setSelectedRows([]);
      setShowBulkDeleteConfirm(false);
      toast.success("Bulk delete successful!");
      refreshData();
    } catch (err) {
      console.error("Bulk Delete Error:", err.message);
      toast.error(
        "Bulk Delete Failed: " + (err.response?.data?.error || err.message),
      );
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file to import");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      setImportLoading(true);
      await dispatch(importDelhiRecords(form));
      setModalOpen(false);
      setFile(null);
    } catch (err) {
      console.error("Import Error:", err.message);
      toast.error(
        "Import Failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setImportLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      await dispatch(exportDelhiRecords());
    } catch (err) {
      console.error("Export Error:", err.message);
      toast.error(
        "Export Failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      await dispatch(deleteDelhiRecord(id));
      refreshData();
    }
  };

  const handleEdit = (row) => {
    setFormData(row);
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    await dispatch(updateDelhiRecord(formData._id, formData));
    setEditModalOpen(false);
    toast("Update successful!", { type: "success" });
    refreshData();
  };

  const actions = [
    {
      icon: <Edit className="h-4 w-4" />,
      className:
        "text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50",
      onClick: handleEdit,
    },
    {
      icon: <Trash2 className="h-4 w-4" />,
      className: "text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50",
      onClick: (row) => handleDelete(row._id),
    },
  ];

  const formatInitiationDate = (value) => {
    if (!value) return "-";
    const dateObj = new Date(value);
    if (isNaN(dateObj.getTime())) return "-";
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = String(dateObj.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Use import date / createdAt for Initiation Date (fallback to Excel date)
  const formattedData = (data || []).map((row) => ({
    ...row,
    initiation_date: formatInitiationDate(
      row.import_date ?? row.createdAt ?? row.date,
    ),
  }));

  // Get userInfo from localStorage and check for usersAdd permission
  let canExport = false;
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    canExport = userInfo && userInfo.role && userInfo.role.usersExport;
  } catch (e) {
    canExport = false;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  Delhi Records
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and view Delhi records data • Click any cell to edit
                  inline
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedRows.length > 0 && (
                  <DangerButton
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    className="flex items-center gap-2"
                  >
                    🗑️ Delete Selected ({selectedRows.length})
                  </DangerButton>
                )}
                <PrimaryButton
                  onClick={() => setModalOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </PrimaryButton>
                {canExport && (
                  <PrimaryButton
                    onClick={handleExport}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>

          <DriveFilters
            searchValue={search}
            onSearchChange={handleSearch}
            searchPlaceholder="Search by name, zone, los, fos, code, reviewer, branch, dept..."
            filters={filterControls}
            total={total}
            columnsClass="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          />
          {selectedRows.length > 0 && (
            <div className="px-6 pb-4">
              <div className="inline-flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-700">
                ✓ {selectedRows.length} record
                {selectedRows.length !== 1 ? "s" : ""} selected
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-hidden">
            {loading ? (
              <div className="py-12">
                <Loading />
              </div>
            ) : (
              <div className="overflow-x-auto" ref={tableRef}>
                <Table
                  data={formattedData}
                  columns={columns}
                  loading={loading}
                  pagination={{ totalRecords: total }}
                  currentPage={page}
                  onPageChange={setPage}
                  actions={actions}
                  selectable={true}
                  selectedRows={selectedRows}
                  onSelectionChange={setSelectedRows}
                  emptyMessage="No Data Found"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                Delete Selected Records
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete {selectedRows.length} selected
                records? This action cannot be undone.
              </p>
              <ModalActionButtons>
                <DangerButton
                  onClick={handleBulkDelete}
                  loading={bulkDeleteLoading}
                  className="flex items-center gap-2"
                >
                  <Trash className="h-4 w-4" />
                  Delete {selectedRows.length} Records
                </DangerButton>
                <SecondaryButton
                  onClick={() => setShowBulkDeleteConfirm(false)}
                >
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}

      {/* Import/Export Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Import / Export Data
              </h3>

              {/* Template Download Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  📋 Excel Template Format
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <h5 className="text-sm font-semibold text-yellow-800 mb-2">
                        ⚠️ Important Notes:
                      </h5>
                      <ul className="text-xs text-yellow-700 space-y-1">
                        <li>• Column names are case-sensitive</li>
                        <li>• Date format: DD/MM/YYYY</li>
                        <li>• Time format: HH:MM AM/PM</li>
                        <li>• TAT should be numeric values</li>
                        <li>• Empty cells will be treated as empty strings</li>
                        <li>• '#N/A' values will be preserved as-is</li>
                        <li>• Zone field defaults to '#N/A' if empty</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <h5 className="text-sm font-semibold text-green-800 mb-2">
                        ✅ Download Template:
                      </h5>
                      <p className="text-xs text-green-700 mb-3">
                        Click below to download a sample Excel template with the
                        correct column structure.
                      </p>
                      <button
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = "/delhi_template.xlsx";
                          link.download = "Delhi_Import_Template.xlsx";
                          link.click();
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        📥 Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* File Upload Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select file to import
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                </div>
              </div>
              <ModalActionButtons className="mt-6">
                <PrimaryButton
                  onClick={handleImport}
                  disabled={!file}
                  className="flex items-center gap-2"
                >
                  ⬆️ Import
                </PrimaryButton>
                {canExport && (
                  <PrimaryButton
                    onClick={handleExport}
                    className="flex items-center gap-2"
                  >
                    ⬇️ Export
                  </PrimaryButton>
                )}
                <SecondaryButton onClick={() => setModalOpen(false)}>
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Record
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.map((field) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                      {field.replace(/_/g, " ")}
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      value={formData[field] || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, [field]: e.target.value })
                      }
                    />
                  </div>
                ))}
              </div>
              <ModalActionButtons className="mt-6">
                <PrimaryButton
                  onClick={handleUpdate}
                  className="flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Update
                </PrimaryButton>
                <SecondaryButton onClick={() => setEditModalOpen(false)}>
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}
      {viewModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Edit Remark
                </h3>

                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={() => copyToClipboard(editRemarkValue)}
                    className="flex items-center gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </PrimaryButton>

                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setRemarkUpdateLoading(true);
                        const updated = {
                          ...formData,
                          remark: editRemarkValue,
                        };
                        await dispatch(
                          updateDelhiRecord(`${formData._id}`, updated),
                        );
                        setViewModalOpen(false);
                        refreshData();
                        toast("Remark updated successfully!", {
                          type: "success",
                        });
                      } catch (err) {
                        console.error(err);
                        toast("Failed to update remark", { type: "error" });
                      } finally {
                        setRemarkUpdateLoading(false);
                      }
                    }}
                    loading={remarkUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>

                  <button
                    onClick={() => setViewModalOpen(false)}
                    disabled={remarkUpdateLoading}
                    className="p-2 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    title="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div
                className="bg-gray-50 p-4 rounded-md border"
                style={{ height: "400px" }}
              >
                <textarea
                  value={editRemarkValue}
                  onChange={(e) => setEditRemarkValue(e.target.value)}
                  className="w-full h-full p-0 border-none bg-transparent text-sm text-gray-700 whitespace-pre-wrap leading-relaxed resize-none focus:outline-none overflow-hidden"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {tatEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit TAT
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setTatUpdateLoading(true);

                        // Get current IST time when TAT is filled
                        const now = new Date();
                        const utc =
                          now.getTime() + now.getTimezoneOffset() * 60000;
                        const istOffset = 5.5 * 60 * 60 * 1000;
                        const currentISTTime = new Date(utc + istOffset);

                        // Format punch_date (YYYY-MM-DD for database)
                        const yyyy = currentISTTime.getFullYear();
                        const mm = String(
                          currentISTTime.getMonth() + 1,
                        ).padStart(2, "0");
                        const dd = String(currentISTTime.getDate()).padStart(
                          2,
                          "0",
                        );
                        const formattedPunchDate = `${yyyy}-${mm}-${dd}`;

                        // Format punch_time (HH:MM AM/PM)
                        let hours = currentISTTime.getHours();
                        const minutes = currentISTTime.getMinutes();
                        const ampm = hours >= 12 ? "PM" : "AM";
                        hours = hours % 12 || 12;
                        const formattedPunchTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;

                        const updated = {
                          ...formData,
                          tat: tatEditValue,
                          punch_date: formattedPunchDate,
                          punch_time: formattedPunchTime,
                          // Automatically set task_status to "Sent To Bank" when TAT is filled
                          task_status:
                            tatEditValue && tatEditValue.trim() !== ""
                              ? "Sent To Bank"
                              : formData.task_status,
                          manualTaskStatus: true, // Mark as manually modified
                        };

                        await dispatch(
                          updateDelhiRecord(`${formData._id}`, updated),
                        );
                        setTatEditModalOpen(false);
                        refreshData();
                        toast(
                          'TAT updated with current punch time and status set to "Sent To Bank"!',
                          { type: "success" },
                        );
                      } catch (err) {
                        console.error(err);
                        toast("Failed to update TAT", { type: "error" });
                        setTatUpdateLoading(false);
                      }
                    }}
                    loading={tatUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTatEditModalOpen(false)}
                    disabled={tatUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="number"
                  value={tatEditValue}
                  onChange={(e) => setTatEditValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter TAT "
                />
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="text-sm">
                    <span className="font-medium text-blue-800">
                      Current IST Time:{" "}
                    </span>
                    <span className="text-blue-700">
                      {getCurrentISTDisplay()}
                    </span>
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    This time will be captured as punch time when you save
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {addressModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  View Address
                </h3>
                <SecondaryButton onClick={() => setAddressModalOpen(false)}>
                  Close
                </SecondaryButton>
              </div>
              <div
                className="bg-gray-50 p-4 rounded-md border"
                style={{ height: "300px" }}
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedAddress}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Task ID Edit Modal */}
      {taskIdEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Task ID
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setTaskIdUpdateLoading(true);
                        const updated = {
                          ...formData,
                          task_id: taskIdEditValue,
                        };
                        await dispatch(
                          updateDelhiRecord(formData._id, updated),
                        );
                        setTaskIdEditModalOpen(false);
                        refreshData();
                        toast("Task ID updated successfully!", {
                          type: "success",
                        });
                      } catch (err) {
                        console.error(err);
                        toast("Failed to update Task ID", { type: "error" });
                      } finally {
                        setTaskIdUpdateLoading(false);
                      }
                    }}
                    loading={taskIdUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTaskIdEditModalOpen(false)}
                    disabled={taskIdUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="number"
                  value={taskIdEditValue}
                  onChange={(e) => setTaskIdEditValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter Task ID"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Status Edit Modal */}
      {taskStatusEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Task Status
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setTaskStatusUpdateLoading(true);
                        const updated = {
                          ...formData,
                          task_status: taskStatusEditValue,
                        };
                        await dispatch(
                          updateDelhiRecord(formData._id, updated),
                        );
                        setTaskStatusEditModalOpen(false);
                        refreshData();
                        toast("Task Status updated successfully!", {
                          type: "success",
                        });
                      } catch (err) {
                        console.error(err);
                        toast("Failed to update Task Status", {
                          type: "error",
                        });
                      } finally {
                        setTaskStatusUpdateLoading(false);
                      }
                    }}
                    loading={taskStatusUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTaskStatusEditModalOpen(false)}
                    disabled={taskStatusUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="text"
                  value={taskStatusEditValue}
                  onChange={(e) => setTaskStatusEditValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter Task Status"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {fosEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit FOS
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setFosUpdateLoading(true);
                        const updated = { ...formData, fos: fosSearchValue };
                        await dispatch(
                          updateDelhiRecord(formData._id, updated),
                        );
                        setFosEditModalOpen(false);
                        setShowSuggestions(false);
                        refreshData();
                        toast("FOS updated successfully!", { type: "success" });
                      } catch (err) {
                        console.error(err);
                        toast("Failed to update FOS", { type: "error" });
                      } finally {
                        setFosUpdateLoading(false);
                      }
                    }}
                    loading={fosUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => {
                      setFosEditModalOpen(false);
                      setShowSuggestions(false);
                      setFieldExecutives([]);
                    }}
                    disabled={fosUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-md border relative">
                <div className="relative">
                  <input
                    type="text"
                    value={fosSearchValue}
                    onChange={(e) => handleFosSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Search Field Executive..."
                    autoComplete="off"
                  />

                  {fosSearchLoading && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    </div>
                  )}

                  {/* Suggestions Dropdown */}
                  {/* Debug info */}
                  {console.log(
                    "Render - showSuggestions:",
                    showSuggestions,
                    "fieldExecutives:",
                    fieldExecutives,
                  )}

                  {/* Suggestions Dropdown */}
                  {showSuggestions && fieldExecutives.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {fieldExecutives.map((executive) => (
                        <div
                          key={executive._id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            setFosSearchValue(executive.name);
                            setShowSuggestions(false);
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {executive.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {executive.email} • {executive.identifier}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showSuggestions &&
                    fieldExecutives.length === 0 &&
                    fosSearchValue.length > 0 &&
                    !fosSearchLoading && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No Field Executives found (Debug: showSuggestions=
                          {showSuggestions.toString()}, fieldExecutives.length=
                          {fieldExecutives.length})
                        </div>
                      </div>
                    )}
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  Type to search for Field Executive users
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DelhiPage;
